import React, { useState, useEffect } from 'react';
import leadApi from '../../api/leadApi';
import { useToast } from '../../contexts/ToastContext';
import settingsApi from '../../api/settingsApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater'; // Import MetaTagsUpdater

function Contact() {
  const { addToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '', // Added phone field
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await settingsApi.getSettings();
        if (response.success && response.data) {
          setSettings(response.data);
        } else {
          setSettings({});
        }
      } catch (error) {
        addToast('Failed to load contact information.', 'error');
      }
    };

    fetchSettings();
  }, [addToast]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (validationErrors[e.target.name]) {
      setValidationErrors(prev => ({ ...prev, [e.target.name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setValidationErrors({});

    try {
      await leadApi.submitLead(formData);
      addToast('Thank you for your message! We will get back to you soon.', 'success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' }); // Clear phone field too
    } catch (error) {
      if (error.response && error.response.status === 400) { // Check for 400 Bad Request
        setValidationErrors(error.response.data.errors || {}); // Backend sends errors in 'errors' key for 400
        addToast(error.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else if (error.response && error.response.status === 422) { // Check for 422 Unprocessable Entity
        setValidationErrors(error.response.data.errors || {}); // Backend sends errors in 'errors' key for 422
        addToast(error.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(error.response?.data?.message || 'Failed to send message. Please try again.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      <MetaTagsUpdater pageKey="Contact" />
      <div className="max-w-6xl mx-auto px-5 py-8">
        <h1 className="text-4xl font-bold text-center text-text-white mb-16 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
          Contact Me
        </h1>

        <div className="flex flex-col md:flex-row gap-12 max-w-5xl mx-auto">
          <div className="md:w-1/2 bg-bg-light p-8 rounded-lg shadow-md border border-border-subtle">
            <h2 className="text-2xl font-semibold text-text-white mb-4">Get in Touch</h2>
            <p className="text-text-gray mb-6">
              Have a question, a project idea, or just want to say hello?
              Feel free to reach out using the contact form or the details below.
            </p>
            <div className="space-y-4">
              {settings?.email && (
                <div>
                  <h3 className="font-semibold text-text-white">Email:</h3>
                  <p className="text-text-gray">{settings.email}</p>
                </div>
              )}
              {settings?.phone && ( // Display phone if available in settings
                <div>
                  <h3 className="font-semibold text-text-white">Phone:</h3>
                  <p className="text-text-gray">{settings.phone}</p>
                </div>
              )}
            </div>
            <div className="mt-8">
              <h3 className="font-semibold text-text-white mb-3">Connect with me:</h3>
              <div className="flex space-x-4">
                {settings?.linkedin_url && (
                  <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-accent-red hover:text-red-700">LinkedIn</a>
                )}
                {settings?.github_url && (
                  <a href={settings.github_url} target="_blank" rel="noopener noreferrer" className="text-accent-red hover:text-red-700">GitHub</a>
                )}
                {settings?.upwork_url && (
                  <a href={settings.upwork_url} target="_blank" rel="noopener noreferrer" className="text-accent-red hover:text-red-700">Upwork</a>
                )}
                {settings?.teams_url && (
                  <a href={settings.teams_url} target="_blank" rel="noopener noreferrer" className="text-accent-red hover:text-red-700">Teams</a>
                )}
              </div>
            </div>
          </div>

          <div className="md:w-1/2 bg-bg-light p-8 rounded-lg shadow-md border border-border-subtle">
            <h2 className="text-2xl font-semibold text-text-white mb-4">Send a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-text-white text-sm font-bold mb-2">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.name ? 'border-red-500' : 'border-border-subtle'}`}
                />
                {validationErrors?.name && <p className="text-red-500 text-xs italic mt-1">{validationErrors.name[0]}</p>}
              </div>
              <div>
                <label htmlFor="email" className="block text-text-white text-sm font-bold mb-2">
                  Email
                </label>
                <input
                  type="text"
                  id="text"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.email ? 'border-red-500' : 'border-border-subtle'}`}
                />
                {validationErrors?.email && <p className="text-red-500 text-xs italic mt-1">{validationErrors.email[0]}</p>}
              </div>
              <div>
                <label htmlFor="phone" className="block text-text-white text-sm font-bold mb-2">
                  Phone
                </label>
                <input
                  type="tel" // Use type="tel" for phone numbers
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.phone ? 'border-red-500' : 'border-border-subtle'}`}
                />
                {validationErrors?.phone && <p className="text-red-500 text-xs italic mt-1">{validationErrors.phone[0]}</p>}
              </div>
              <div>
                <label htmlFor="subject" className="block text-text-white text-sm font-bold mb-2">
                  Subject
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.subject ? 'border-red-500' : 'border-border-subtle'}`}
                />
                {validationErrors?.subject && <p className="text-red-500 text-xs italic mt-1">{validationErrors.subject[0]}</p>}
              </div>
              <div>
                <label htmlFor="message" className="block text-text-white text-sm font-bold mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className={`shadow appearance-none border rounded w-full py-2 px-3 text-text-white leading-tight focus:outline-none focus:shadow-outline bg-bg-dark ${validationErrors?.message ? 'border-red-500' : 'border-border-subtle'}`}
                ></textarea>
                {validationErrors?.message && <p className="text-red-500 text-xs italic mt-1">{validationErrors.message[0]}</p>}
              </div>
              <button
                type="submit"
                className="bg-accent-red hover:bg-red-700 text-text-white font-bold py-2 px-4 rounded-full focus:outline-none focus:shadow-outline transition-colors duration-300"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;